// ============================================
// src/components/PrepGuide/talkingPointsEngine.ts
// Classifies a case and builds dynamic talking point configs
// ============================================

import type { Case } from "../../types/caseView";

// ── Classification types ────────────────────────────────────

export type ServiceType =
  | "imaging"
  | "procedure"
  | "medication"
  | "dme"
  | "level_of_care"
  | "referral"
  | "unknown";

export type SpecialtyDomain =
  | "obgyn"
  | "neuro"
  | "msk"
  | "cardio"
  | "pulm"
  | "gi"
  | "onc"
  | "pain"
  | "endo"
  | "derm"
  | "psych"
  | "peds"
  | "uro"
  | "allergy"
  | "general";

export interface CaseClassification {
  serviceType: ServiceType;
  specialtyDomain: SpecialtyDomain;
  hasFailedTherapy: boolean;
  hasMedFailures: boolean;
  hasSeverityFeatures: boolean;
  hasGuidelineMatch: boolean;
  riskFactors: string[];
}

// ── Pill / category config types ────────────────────────────

export interface TalkingPointPill {
  id: string;
  label: string;
  isOther?: boolean;
  /** Pre-select this pill if these keywords appear in case data */
  autoSelectKeywords?: string[];
}

export interface TalkingPointExtra {
  id: string;
  label: string;
  type: "text" | "number";
  placeholder: string;
  suffix?: string;
  width: "xs" | "sm" | "md";
  showWhen?: "hasSelection";
}

export interface TalkingPointCategory {
  id: string;
  label: string;
  description: string;
  order: number;
  pills: TalkingPointPill[];
  extras?: TalkingPointExtra[];
  /** Sentence template for the speakable summary */
  sentencePrefix: string;
  /** Auto-expand this category on first render */
  autoExpand?: boolean;
}

// ── Keyword maps for classification ─────────────────────────

const SERVICE_KEYWORDS: Record<ServiceType, string[]> = {
  imaging: [
    "mri", "ct", "ultrasound", "x-ray", "xray", "imaging", "scan",
    "sonogram", "mammogram", "pet", "dexa", "arthrogram", "fluoroscopy",
    "echo", "echocardiogram", "angiogram", "venogram",
    "pelvic ultrasound", "transvaginal", "abdominal ultrasound",
  ],
  procedure: [
    "surgery", "surgical", "injection", "ablation", "biopsy",
    "arthroscopy", "endoscopy", "colonoscopy", "catheterization",
    "implant", "stimulator", "fusion", "decompression", "excision",
    "hysterectomy", "myomectomy", "laparoscopy", "embolization",
    "stent", "block", "rhizotomy", "discectomy", "laminectomy",
  ],
  medication: [
    "medication", "drug", "prescription", "biologic", "infusion",
    "chemotherapy", "immunotherapy", "injectable", "humira", "enbrel",
    "dupixent", "ozempic", "mounjaro", "botox", "antidepressant",
  ],
  dme: [
    "dme", "brace", "wheelchair", "cpap", "bipap", "prosthetic",
    "orthotic", "walker", "hospital bed", "oxygen",
  ],
  level_of_care: [
    "inpatient", "admission", "observation", "icu", "skilled nursing",
    "rehab facility", "step-down", "transfer",
  ],
  referral: [
    "referral", "specialist", "consultation", "second opinion",
  ],
  unknown: [],
};

const SPECIALTY_KEYWORDS: Record<SpecialtyDomain, string[]> = {
  obgyn: [
    "uterine", "uterus", "ovarian", "ovary", "cervical", "cervix",
    "menstrual", "menorrhagia", "metrorrhagia", "aub", "abnormal uterine",
    "bleeding", "fibroids", "endometriosis", "adenomyosis", "pcos",
    "pregnancy", "prenatal", "postpartum", "pelvic mass", "gynecologic",
    "hysterectomy", "myomectomy", "colposcopy", "pap", "hpv",
    "obstetric", "ob/gyn", "obgyn", "gyn",
  ],
  neuro: [
    "brain", "cerebral", "neurologic", "headache", "migraine", "seizure",
    "epilepsy", "stroke", "tia", "neuropathy", "ms ", "multiple sclerosis",
    "parkinson", "dementia", "alzheimer", "radiculopathy", "myelopathy",
    "cranial", "intracranial",
  ],
  msk: [
    "spine", "spinal", "lumbar", "cervical spine", "thoracic",
    "disc", "disk", "stenosis", "spondylosis", "spondylolisthesis",
    "joint", "knee", "hip", "shoulder", "rotator", "meniscus", "acl",
    "fracture", "osteoarthritis", "musculoskeletal", "back pain",
    "neck pain",
  ],
  cardio: [
    "cardiac", "heart", "coronary", "arrhythmia", "afib",
    "cardiomyopathy", "valve", "aortic", "mitral", "chf",
    "heart failure", "angina", "palpitation", "hypertension",
    "echocardiogram",
  ],
  pulm: [
    "lung", "pulmonary", "respiratory", "asthma", "copd", "pneumonia",
    "pleural", "bronch", "sleep apnea", "oxygen", "ventilat",
    "interstitial", "fibrosis",
  ],
  gi: [
    "gastro", "gi ", "abdominal", "liver", "hepatic", "colon", "bowel",
    "ibd", "crohn", "ulcerative colitis", "gerd", "esophag",
    "pancrea", "gallbladder", "biliary",
  ],
  onc: [
    "cancer", "tumor", "malignant", "malignancy", "oncolog", "metasta",
    "carcinoma", "lymphoma", "leukemia", "sarcoma", "neoplasm",
    "chemotherapy", "radiation therapy",
  ],
  pain: [
    "chronic pain", "pain management", "pain medicine", "opioid",
    "spinal cord stimulator", "intrathecal", "nerve block",
    "facet", "epidural", "trigger point",
  ],
  endo: [
    "diabetes", "thyroid", "endocrine", "insulin", "a1c",
    "adrenal", "pituitary", "testosterone", "hormone",
  ],
  derm: [
    "skin", "dermatolog", "psoriasis", "eczema", "melanoma",
    "acne", "rash", "lesion", "biopsy skin",
  ],
  psych: [
    "psychiatric", "mental health", "depression", "anxiety", "bipolar",
    "schizophren", "ptsd", "adhd", "behavioral",
  ],
  peds: [
    "pediatric", "child", "infant", "neonatal", "adolescent",
  ],
  uro: [
    "urolog", "kidney", "renal", "bladder", "prostate", "urinary",
    "hematuria", "nephro",
  ],
  allergy: [
    "allergy", "allergic", "anaphylaxis", "immunotherapy",
    "desensitiz",
  ],
  general: [],
};

const SEVERITY_KEYWORDS = [
  "emergent", "urgent", "acute", "severe", "progressive", "worsening",
  "rapidly", "debilitating", "incapacitating", "intractable",
  "uncontrolled", "refractory", "cancer", "malignancy", "anemia",
  "hemorrhage", "unstable",
];

// ── Classifier ──────────────────────────────────────────────

function matchKeywords(text: string, keywords: string[]): boolean {
  const lower = text.toLowerCase();
  return keywords.some((kw) => lower.includes(kw.toLowerCase()));
}

function countKeywordMatches(text: string, keywords: string[]): number {
  const lower = text.toLowerCase();
  return keywords.filter((kw) => lower.includes(kw.toLowerCase())).length;
}

export function classifyCase(caseData: Case): CaseClassification {
  // Combine all text fields for broad matching
  const allText = [
    caseData.request_type,
    caseData.request_details,
    caseData.diagnoses,
    caseData.additional_context,
    caseData.clinical_history,
    caseData.comorbidities,
  ]
    .filter(Boolean)
    .join(" ");

  // — Service type
  let serviceType: ServiceType = "unknown";
  let bestServiceScore = 0;
  for (const [type, keywords] of Object.entries(SERVICE_KEYWORDS)) {
    if (type === "unknown") continue;
    const requestText = `${caseData.request_type || ""} ${caseData.request_details || ""}`;
    const score = countKeywordMatches(requestText, keywords);
    if (score > bestServiceScore) {
      bestServiceScore = score;
      serviceType = type as ServiceType;
    }
  }

  // — Specialty domain
  let specialtyDomain: SpecialtyDomain = "general";
  let bestSpecialtyScore = 0;
  for (const [domain, keywords] of Object.entries(SPECIALTY_KEYWORDS)) {
    if (domain === "general") continue;
    const score = countKeywordMatches(allText, keywords);
    if (score > bestSpecialtyScore) {
      bestSpecialtyScore = score;
      specialtyDomain = domain as SpecialtyDomain;
    }
  }

  // — Failed therapy
  const triedText = caseData.tried_already || "";
  const hasFailedTherapy = triedText.trim().length > 0;

  // — Medication failures
  let hasMedFailures = false;
  if (caseData.prior_medications) {
    try {
      const meds = JSON.parse(caseData.prior_medications);
      hasMedFailures = Array.isArray(meds) && meds.length > 0;
    } catch {
      hasMedFailures = caseData.prior_medications.trim().length > 0;
    }
  }

  // — Severity features
  const hasSeverityFeatures = matchKeywords(allText, SEVERITY_KEYWORDS);

  // — Risk factors from comorbidities
  const riskFactors: string[] = [];
  if (caseData.comorbidities) {
    riskFactors.push(
      ...caseData.comorbidities
        .split(/[,;]/)
        .map((s) => s.trim())
        .filter(Boolean)
    );
  }

  // — Guideline match (heuristic: check for criteria_system or known guideline terms)
  const guidelineText = `${caseData.criteria_system || ""} ${allText}`;
  const hasGuidelineMatch = matchKeywords(guidelineText, [
    "acog", "acr", "apta", "aha", "acc", "nccn", "asco", "aua",
    "aaos", "aan", "nice", "uspstf", "interqual", "mcg", "cms",
    "guideline", "criteria", "appropriateness",
  ]);

  return {
    serviceType,
    specialtyDomain,
    hasFailedTherapy,
    hasMedFailures,
    hasSeverityFeatures,
    hasGuidelineMatch,
    riskFactors,
  };
}

// ── Dynamic category builders ───────────────────────────────
// Each function returns a TalkingPointCategory or null

function buildDurationPersistence(
  classification: CaseClassification,
  caseData: Case
): TalkingPointCategory | null {
  if (classification.serviceType !== "imaging" && classification.serviceType !== "referral") return null;
  return {
    id: "durationPersistence",
    label: "Persistent Symptoms",
    description: "Duration, recurrence, and clinical trajectory",
    order: 1,
    sentencePrefix: "The patient has had persistent symptoms including",
    autoExpand: true,
    pills: [
      { id: "chronic", label: "Symptoms >6 weeks" },
      { id: "worsening", label: "Progressive worsening" },
      { id: "recurrent", label: "Recurrent episodes" },
      { id: "no_improvement", label: "No spontaneous improvement" },
    ],
    extras: [
      {
        id: "durationWeeks",
        label: "Duration",
        type: "number",
        placeholder: "#",
        suffix: "weeks",
        width: "xs",
      },
    ],
  };
}

function buildFailedConservative(
  classification: CaseClassification,
  caseData: Case
): TalkingPointCategory | null {
  if (!classification.hasFailedTherapy && classification.serviceType === "medication") return null;

  // Build pills from what's actually in the case
  const basePills: TalkingPointPill[] = [];
  const tried = (caseData.tried_already || "").toLowerCase();

  // Always offer common options but auto-mark what's documented
  const conservativeOptions: Array<{ id: string; label: string; keywords: string[] }> = [
    { id: "pt", label: "Physical therapy", keywords: ["physical therapy", "pt", "physiotherapy"] },
    { id: "nsaids", label: "NSAIDs", keywords: ["nsaid", "ibuprofen", "naproxen", "meloxicam", "diclofenac", "anti-inflammatory"] },
    { id: "steroids", label: "Oral steroids", keywords: ["steroid", "prednisone", "medrol", "methylprednisolone"] },
    { id: "injections", label: "Injections", keywords: ["injection", "cortisone", "steroid injection"] },
    { id: "rest", label: "Activity modification", keywords: ["rest", "activity modification", "activity restriction"] },
    { id: "otc", label: "OTC medications", keywords: ["otc", "over-the-counter", "tylenol", "acetaminophen"] },
    { id: "hormonal", label: "Hormonal therapy", keywords: ["hormone", "hormonal", "birth control", "oral contraceptive", "progesterone", "depot", "iud"] },
    { id: "antibiotics", label: "Antibiotics", keywords: ["antibiotic", "amoxicillin", "azithromycin"] },
    { id: "observation", label: "Watchful waiting", keywords: ["observation", "watchful", "monitoring", "wait"] },
  ];

  for (const opt of conservativeOptions) {
    const autoSelect = opt.keywords?.some((kw) => tried.includes(kw)) ?? false;
    basePills.push({
      id: opt.id,
      label: opt.label,
      autoSelectKeywords: autoSelect ? opt.keywords : undefined,
    });
  }

  return {
    id: "failedConservativeTx",
    label: "Failed First-Line Therapy",
    description: "First-line treatments tried without adequate response",
    order: 2,
    sentencePrefix: "First-line treatments have failed including",
    pills: basePills,
    extras: [
      {
        id: "durationWeeks",
        label: "Duration",
        type: "number",
        placeholder: "#",
        suffix: "weeks of treatment",
        width: "xs",
      },
    ],
  };
}

function buildDiagnosticUncertainty(
  classification: CaseClassification,
  _caseData: Case
): TalkingPointCategory | null {
  if (classification.serviceType !== "imaging") return null;

  // Build specialty-specific rule-out options
  const pills: TalkingPointPill[] = [];
  const { specialtyDomain } = classification;

  if (specialtyDomain === "obgyn") {
    pills.push(
      { id: "structural", label: "Must exclude serious uterine pathology" },
      { id: "malignancy", label: "Rule out endometrial malignancy" },
      { id: "ectopic", label: "Rule out ectopic pregnancy" },
      { id: "adnexal", label: "Adnexal mass evaluation" },
      { id: "fibroids", label: "Fibroid mapping" },
    );
  } else if (specialtyDomain === "neuro") {
    pills.push(
      { id: "mass", label: "Rule out intracranial mass" },
      { id: "demyelination", label: "Rule out demyelinating disease" },
      { id: "vascular", label: "Vascular abnormality evaluation" },
      { id: "compression", label: "Nerve root compression" },
    );
  } else if (specialtyDomain === "msk") {
    pills.push(
      { id: "disc", label: "Disc herniation evaluation" },
      { id: "stenosis", label: "Spinal stenosis assessment" },
      { id: "fracture", label: "Occult fracture rule-out" },
      { id: "tumor", label: "Rule out osseous tumor" },
      { id: "instability", label: "Structural instability" },
    );
  } else if (specialtyDomain === "cardio") {
    pills.push(
      { id: "structural", label: "Structural heart disease" },
      { id: "valvular", label: "Valvular disease assessment" },
      { id: "cardiomyopathy", label: "Cardiomyopathy evaluation" },
      { id: "pericardial", label: "Pericardial disease" },
    );
  } else if (specialtyDomain === "gi") {
    pills.push(
      { id: "mass", label: "Rule out GI malignancy" },
      { id: "obstruction", label: "Obstruction evaluation" },
      { id: "hepatic", label: "Hepatic disease assessment" },
      { id: "inflammatory", label: "Inflammatory disease evaluation" },
    );
  } else if (specialtyDomain === "onc") {
    pills.push(
      { id: "staging", label: "Staging assessment" },
      { id: "progression", label: "Disease progression evaluation" },
      { id: "metastasis", label: "Metastatic workup" },
      { id: "treatment_response", label: "Treatment response assessment" },
    );
  } else if (specialtyDomain === "pulm") {
    pills.push(
      { id: "pe", label: "Rule out pulmonary embolism" },
      { id: "mass", label: "Pulmonary mass evaluation" },
      { id: "ild", label: "Interstitial lung disease" },
      { id: "effusion", label: "Pleural effusion assessment" },
    );
  } else if (specialtyDomain === "uro") {
    pills.push(
      { id: "mass", label: "Rule out renal mass" },
      { id: "obstruction", label: "Urinary obstruction evaluation" },
      { id: "stone", label: "Nephrolithiasis assessment" },
      { id: "hematuria", label: "Hematuria workup" },
    );
  } else {
    // General fallback
    pills.push(
      { id: "rule_out", label: "Rule out serious pathology" },
      { id: "baseline", label: "Baseline diagnostic assessment" },
      { id: "progression", label: "Evaluate disease progression" },
    );
  }
  return {
    id: "ruleOutPathology",
    label: "Rule Out Serious Pathology",
    description: "What must be ruled out or confirmed?",
    order: 3,
    sentencePrefix: "Imaging is necessary because",
    pills,
    extras: [
      {
        id: "findings",
        label: "Supporting findings",
        type: "text",
        placeholder: "Exam or lab findings supporting workup",
        width: "md",
        showWhen: "hasSelection",
      },
    ],
  };
}

function buildGuidelineSupport(
  classification: CaseClassification,
  caseData: Case
): TalkingPointCategory | null {
  if (!classification.hasGuidelineMatch) return null;

  const pills: TalkingPointPill[] = [];
  const { specialtyDomain, serviceType } = classification;
  const criteriaSystem = (caseData.criteria_system || "").toLowerCase();

  // Specialty-specific guidelines
  if (specialtyDomain === "obgyn") {
    pills.push(
      { id: "acog", label: "ACOG recommends this as first-line" },
      { id: "acog_pb", label: "ACOG Practice Bulletin supports indication" },
    );
  }
  if (specialtyDomain === "msk" || specialtyDomain === "pain") {
    pills.push(
      { id: "acr", label: "ACR Appropriateness Criteria met" },
      { id: "apta", label: "APTA clinical guideline supports this" },
    );
  }
  if (specialtyDomain === "neuro") {
    pills.push(
      { id: "aan", label: "AAN guideline supports indication" },
      { id: "acr", label: "ACR Appropriateness Criteria met" },
    );
  }
  if (specialtyDomain === "cardio") {
    pills.push(
      { id: "aha", label: "AHA/ACC guideline recommendation" },
      { id: "auc", label: "Appropriate Use Criteria met" },
    );
  }
  if (specialtyDomain === "onc") {
    pills.push(
      { id: "nccn", label: "NCCN guideline supports this" },
    );
  }
  if (specialtyDomain === "pulm") {
    pills.push(
      { id: "ats", label: "ATS guideline supports this" },
    );
  }

  // Insurer criteria systems
  if (criteriaSystem.includes("interqual")) {
    pills.push({ id: "interqual", label: "Meets InterQual criteria" });
  }
  if (criteriaSystem.includes("mcg")) {
    pills.push({ id: "mcg", label: "Meets MCG criteria" });
  }
  if (criteriaSystem.includes("cms")) {
    pills.push({ id: "cms", label: "Meets CMS coverage criteria" });
  }

  // Generic fallback
  if (pills.length === 0) {
    pills.push(
      { id: "society", label: "Society guideline supports indication" },
      { id: "criteria_met", label: "Published criteria met" },
    );
  }
  return {
    id: "guidelineSupport",
    label: "Guideline Match",
    description: "Guideline and criteria alignment",
    order: 4,
    sentencePrefix: "This request is supported by guidelines —",
    pills,
  };
}

function buildRiskOfDelay(
  classification: CaseClassification,
  _caseData: Case
): TalkingPointCategory | null {
  const pills: TalkingPointPill[] = [];
  const { specialtyDomain } = classification;

  // Every label must name a CONCRETE clinical harm — no abstract language.
  // ≤10 words. Speakable. Slightly urgent tone.
  if (specialtyDomain === "obgyn") {
    pills.push(
      { id: "cancer_delay", label: "Missed endometrial malignancy" },
      { id: "anemia", label: "Progressive anemia requiring transfusion" },
      { id: "hemorrhage", label: "Acute hemorrhage" },
      { id: "fertility", label: "Irreversible fertility loss" },
    );
  } else if (specialtyDomain === "neuro") {
    pills.push(
      { id: "permanent_deficit", label: "Permanent neurological deficit" },
      { id: "herniation", label: "Uncal herniation" },
      { id: "progression", label: "Irreversible neurologic loss" },
      { id: "emergency", label: "Preventable emergency presentation" },
    );
  } else if (specialtyDomain === "msk") {
    pills.push(
      { id: "permanent_damage", label: "Irreversible joint destruction" },
      { id: "surgery", label: "Preventable emergency surgery" },
      { id: "chronic_pain", label: "Chronic pain syndrome" },
      { id: "disability", label: "Permanent functional disability" },
    );
  } else if (specialtyDomain === "cardio") {
    pills.push(
      { id: "mi", label: "Myocardial infarction" },
      { id: "sudden_death", label: "Sudden cardiac death" },
      { id: "decompensation", label: "Acute decompensated heart failure" },
      { id: "stroke", label: "Cardioembolic stroke" },
    );
  } else if (specialtyDomain === "onc") {
    pills.push(
      { id: "staging_delay", label: "Stage migration from delayed diagnosis" },
      { id: "progression", label: "Uncontrolled disease progression" },
      { id: "survival", label: "Reduced overall survival" },
    );
  } else if (specialtyDomain === "pulm") {
    pills.push(
      { id: "respiratory_failure", label: "Acute respiratory failure" },
      { id: "cancer_delay", label: "Missed pulmonary malignancy" },
      { id: "hypoxia", label: "Hypoxemic end-organ damage" },
    );
  } else if (specialtyDomain === "gi") {
    pills.push(
      { id: "perforation", label: "Bowel perforation" },
      { id: "obstruction", label: "Complete bowel obstruction" },
      { id: "cancer_delay", label: "Missed GI malignancy" },
      { id: "hemorrhage", label: "GI hemorrhage" },
    );
  } else if (specialtyDomain === "uro") {
    pills.push(
      { id: "obstruction", label: "Complete urinary obstruction" },
      { id: "renal_damage", label: "Irreversible renal damage" },
      { id: "cancer_delay", label: "Missed renal or bladder malignancy" },
      { id: "sepsis", label: "Urosepsis" },
    );
  } else if (specialtyDomain === "pain") {
    pills.push(
      { id: "chronic_pain", label: "Central sensitization and chronic pain" },
      { id: "opioid", label: "Escalating opioid dependence" },
      { id: "disability", label: "Permanent functional disability" },
      { id: "emergency", label: "Preventable emergency visit" },
    );
  } else if (specialtyDomain === "endo") {
    pills.push(
      { id: "dka", label: "Diabetic ketoacidosis" },
      { id: "organ_damage", label: "End-organ damage progression" },
      { id: "hospitalization", label: "Preventable hospitalization" },
    );
  } else if (specialtyDomain === "psych") {
    pills.push(
      { id: "hospitalization", label: "Psychiatric hospitalization" },
      { id: "crisis", label: "Acute psychiatric crisis" },
      { id: "functional_decline", label: "Severe functional decline" },
    );
  } else {
    pills.push(
      { id: "missed_dx", label: "Missed serious diagnosis" },
      { id: "hospitalization", label: "Preventable hospitalization" },
      { id: "emergency", label: "Preventable emergency visit" },
      { id: "organ_damage", label: "Irreversible organ damage" },
    );
  }

  return {
    id: "riskOfDelay",
    label: "Risk of Delay",
    description: "Concrete clinical harms if denied or delayed",
    order: 5,
    sentencePrefix: "Delay risks",
    pills,
  };
}

function buildFunctionalLimitation(
  classification: CaseClassification,
  _caseData: Case
): TalkingPointCategory | null {
  if (classification.serviceType !== "procedure" && classification.serviceType !== "dme") return null;

  return {
    id: "functionalLimitation",
    label: "Functional Limitation",
    description: "How is this affecting daily life?",
    order: 1,
    sentencePrefix: "The patient has functional limitations including",
    autoExpand: true,
    pills: [
      { id: "adl", label: "Cannot perform ADLs" },
      { id: "work", label: "Unable to work" },
      { id: "mobility", label: "Impaired mobility" },
      { id: "sleep", label: "Sleep disruption" },
      { id: "self_care", label: "Cannot perform self-care" },
    ],
    extras: [
      {
        id: "functional_score",
        label: "Functional score",
        type: "text",
        placeholder: "e.g., ODI 62%",
        width: "md",
        showWhen: "hasSelection",
      },
    ],
  };
}

function buildImagingFindings(
  classification: CaseClassification,
  _caseData: Case
): TalkingPointCategory | null {
  if (classification.serviceType !== "procedure") return null;

  return {
    id: "imagingFindings",
    label: "Imaging Rationale",
    description: "Imaging findings supporting this intervention",
    order: 4,
    sentencePrefix: "Imaging confirms",
    pills: [
      { id: "positive_mri", label: "MRI confirms underlying pathology" },
      { id: "ct_findings", label: "CT findings correlate" },
      { id: "xray", label: "X-ray shows abnormality" },
      { id: "ultrasound", label: "Ultrasound findings" },
    ],
    extras: [
      {
        id: "findings_detail",
        label: "Key finding",
        type: "text",
        placeholder: "e.g., L4-5 disc extrusion",
        width: "md",
        showWhen: "hasSelection",
      },
    ],
  };
}

function buildImagingRationale(
  classification: CaseClassification,
  _caseData: Case
): TalkingPointCategory | null {
  if (classification.serviceType !== "imaging") return null;

  const pills: TalkingPointPill[] = [];
  const { specialtyDomain } = classification;

  // Specialty-specific imaging rationale chips
  if (specialtyDomain === "obgyn") {
    pills.push(
      { id: "firstline", label: "Pelvic ultrasound is first-line imaging" },
      { id: "guideline", label: "Recommended by ACOG guidelines" },
      { id: "standard", label: "Standard diagnostic approach for AUB" },
      { id: "noninvasive", label: "Non-invasive initial evaluation" },
    );
  } else if (specialtyDomain === "neuro") {
    pills.push(
      { id: "firstline", label: "MRI is first-line neuroimaging" },
      { id: "guideline", label: "AAN guideline-supported study" },
      { id: "standard", label: "Standard workup for this presentation" },
      { id: "noninvasive", label: "Non-invasive diagnostic approach" },
    );
  } else if (specialtyDomain === "msk" || specialtyDomain === "pain") {
    pills.push(
      { id: "firstline", label: "First-line imaging for this complaint" },
      { id: "guideline", label: "ACR Appropriateness Criteria met" },
      { id: "standard", label: "Standard diagnostic evaluation" },
      { id: "correlation", label: "Imaging needed for clinical correlation" },
    );
  } else if (specialtyDomain === "cardio") {
    pills.push(
      { id: "firstline", label: "First-line cardiac imaging study" },
      { id: "guideline", label: "AHA/ACC guideline-supported" },
      { id: "standard", label: "Standard of care evaluation" },
    );
  } else if (specialtyDomain === "pulm") {
    pills.push(
      { id: "firstline", label: "First-line pulmonary imaging" },
      { id: "guideline", label: "Guideline-supported study" },
      { id: "standard", label: "Standard diagnostic approach" },
    );
  } else if (specialtyDomain === "onc") {
    pills.push(
      { id: "staging", label: "Standard staging imaging" },
      { id: "guideline", label: "NCCN guideline-supported study" },
      { id: "surveillance", label: "Guideline-required surveillance" },
    );
  } else {
    pills.push(
      { id: "firstline", label: "First-line imaging for this diagnosis" },
      { id: "guideline", label: "Guideline-recommended study" },
      { id: "standard", label: "Standard diagnostic approach" },
      { id: "noninvasive", label: "Non-invasive initial evaluation" },
    );
  }

  return {
    id: "imagingRationale",
    label: "Imaging Rationale",
    description: "Why this specific imaging study is indicated",
    order: 4,
    sentencePrefix: "This imaging is indicated because",
    pills,
  };
}

function buildPriorMedFailures(
  classification: CaseClassification,
  caseData: Case
): TalkingPointCategory | null {
  if (classification.serviceType !== "medication") return null;

  const pills: TalkingPointPill[] = [];

  // Pull from actual prior medications if available
  if (caseData.prior_medications) {
    try {
      const meds = JSON.parse(caseData.prior_medications);
      if (Array.isArray(meds)) {
        for (const med of meds.slice(0, 6)) {
          const name = med.displayName || med.name;
          if (name) {
            pills.push({
              id: `med_${name.toLowerCase().replace(/\s+/g, "_")}`,
              label: `${name} — failed/intolerant`,
            });
          }
        }
      }
    } catch {
      // ignore parse errors
    }
  }

  pills.push(
    { id: "step_therapy", label: "Step therapy requirements completed" },
    { id: "contraindication", label: "Alternatives contraindicated" },
    { id: "adverse_effect", label: "Adverse effects to alternatives" },
  );

  return {
    id: "priorMedFailures",
    label: "Prior Medication Trials",
    description: "Medications tried and why they didn't work",
    order: 2,
    sentencePrefix: "Prior medication trials have failed including",
    autoExpand: true,
    pills,
    extras: [
      {
        id: "details",
        label: "Details",
        type: "text",
        placeholder: "Specific reason for failure or intolerance",
        width: "md",
        showWhen: "hasSelection",
      },
    ],
  };
}

function buildContraindicationsToAlternatives(
  classification: CaseClassification,
  _caseData: Case
): TalkingPointCategory | null {
  if (classification.serviceType !== "medication") return null;

  return {
    id: "contraindications",
    label: "Alternative Contraindications",
    description: "Why the preferred alternatives cannot be used",
    order: 3,
    sentencePrefix: "The patient cannot use alternative medications due to",
    pills: [
      { id: "allergy", label: "Drug allergy" },
      { id: "organ_function", label: "Organ function impairment" },
      { id: "interaction", label: "Drug interaction" },
      { id: "comorbidity", label: "Comorbidity contraindication" },
      { id: "pregnancy", label: "Pregnancy/reproductive risk" },
      { id: "age", label: "Age-related contraindication" },
    ],
  };
}

function buildRiskIfUntreated(
  classification: CaseClassification,
  _caseData: Case
): TalkingPointCategory | null {
  if (classification.serviceType !== "medication") return null;

  const { specialtyDomain } = classification;
  const pills: TalkingPointPill[] = [];

  // Concrete patient harms only — no operational or abstract language.
  if (specialtyDomain === "psych") {
    pills.push(
      { id: "hospitalization", label: "Psychiatric hospitalization" },
      { id: "crisis", label: "Acute psychiatric crisis" },
      { id: "functional_decline", label: "Severe functional decline" },
    );
  } else if (specialtyDomain === "endo") {
    pills.push(
      { id: "dka", label: "Diabetic ketoacidosis" },
      { id: "organ_damage", label: "End-organ damage progression" },
      { id: "hospitalization", label: "Preventable hospitalization" },
    );
  } else if (specialtyDomain === "onc") {
    pills.push(
      { id: "progression", label: "Uncontrolled disease progression" },
      { id: "hospitalization", label: "Preventable hospitalization" },
      { id: "survival", label: "Reduced overall survival" },
    );
  } else if (specialtyDomain === "cardio") {
    pills.push(
      { id: "mi", label: "Myocardial infarction" },
      { id: "decompensation", label: "Acute decompensated heart failure" },
      { id: "stroke", label: "Stroke" },
    );
  } else if (specialtyDomain === "pulm") {
    pills.push(
      { id: "exacerbation", label: "Severe exacerbation requiring intubation" },
      { id: "hospitalization", label: "Preventable hospitalization" },
      { id: "respiratory_failure", label: "Acute respiratory failure" },
    );
  } else if (specialtyDomain === "pain") {
    pills.push(
      { id: "opioid", label: "Escalating opioid dependence" },
      { id: "chronic_pain", label: "Central sensitization" },
      { id: "emergency", label: "Preventable emergency visit" },
      { id: "disability", label: "Permanent functional disability" },
    );
  } else {
    pills.push(
      { id: "progression", label: "Uncontrolled disease progression" },
      { id: "hospitalization", label: "Preventable hospitalization" },
      { id: "emergency", label: "Preventable emergency visit" },
      { id: "organ_damage", label: "Irreversible organ damage" },
    );
  }

  return {
    id: "riskIfUntreated",
    label: "Risk of Delay",
    description: "Concrete clinical harms if this medication is withheld",
    order: 5,
    sentencePrefix: "Withholding this medication risks",
    pills,
  };
}

// ── Specialty overlays ──────────────────────────────────────
// These ADD specialty-specific points on top of the service-type base

function buildObgynOverlay(
  classification: CaseClassification,
  _caseData: Case
): TalkingPointCategory | null {
  if (classification.specialtyDomain !== "obgyn") return null;

  return {
    id: "obgynSpecific",
    label: "OB/GYN Specifics",
    description: "Specialty-specific clinical arguments",
    order: 2.5,
    sentencePrefix: "From a gynecologic standpoint,",
    pills: [
      { id: "bleeding_duration", label: "Bleeding duration exceeds threshold" },
      { id: "anemia_risk", label: "Anemia or hemodynamic risk" },
      { id: "structural_ruleout", label: "Must exclude serious pathology" },
      { id: "fertility_impact", label: "Fertility preservation concern" },
      { id: "age_screening", label: "Age-appropriate screening indication" },
    ],
    extras: [
      {
        id: "hgb",
        label: "Hgb",
        type: "text",
        placeholder: "e.g., 9.2",
        suffix: "g/dL",
        width: "xs",
      },
    ],
  };
}

function buildNeuroOverlay(
  classification: CaseClassification,
  _caseData: Case
): TalkingPointCategory | null {
  if (classification.specialtyDomain !== "neuro") return null;

  return {
    id: "neuroSpecific",
    label: "Neuro Specifics",
    description: "Documented neurological findings",
    order: 2.5,
    sentencePrefix: "Neurological evaluation demonstrates",
    pills: [
      { id: "objective_deficit", label: "Objective neurological deficit" },
      { id: "red_flags", label: "Red flag symptoms present" },
      { id: "progressive", label: "Progressive deterioration documented" },
      { id: "emergent", label: "Emergent features present" },
      { id: "lateralizing", label: "Lateralizing signs on exam" },
    ],
    extras: [
      {
        id: "onset",
        label: "Onset",
        type: "text",
        placeholder: "e.g., 2 weeks ago",
        width: "md",
      },
    ],
  };
}

function buildMskOverlay(
  classification: CaseClassification,
  _caseData: Case
): TalkingPointCategory | null {
  if (classification.specialtyDomain !== "msk" && classification.specialtyDomain !== "pain") return null;

  return {
    id: "mskSpecific",
    label: "MSK Specifics",
    description: "MSK-specific exam and history findings",
    order: 2.5,
    sentencePrefix: "Musculoskeletal evaluation shows",
    pills: [
      { id: "limited_rom", label: "Limited range of motion" },
      { id: "positive_test", label: "Positive provocative testing" },
      { id: "radiculopathy", label: "Radiculopathy pattern" },
      { id: "weakness", label: "Documented motor weakness" },
      { id: "instability", label: "Joint instability" },
    ],
    extras: [
      {
        id: "pain_score",
        label: "Pain",
        type: "number",
        placeholder: "0-10",
        suffix: "/10",
        width: "xs",
      },
    ],
  };
}

function buildCardioOverlay(
  classification: CaseClassification,
  _caseData: Case
): TalkingPointCategory | null {
  if (classification.specialtyDomain !== "cardio") return null;

  return {
    id: "cardioSpecific",
    label: "Cardiac Specifics",
    description: "Cardiac-specific findings and risk factors",
    order: 2.5,
    sentencePrefix: "Cardiovascular assessment shows",
    pills: [
      { id: "symptoms", label: "Symptomatic presentation" },
      { id: "ef_reduced", label: "Reduced ejection fraction" },
      { id: "biomarkers", label: "Elevated cardiac biomarkers" },
      { id: "ecg_abnormal", label: "ECG abnormalities" },
      { id: "risk_score", label: "Elevated cardiovascular risk score" },
    ],
  };
}

// ── Main assembly function ──────────────────────────────────

export function buildTalkingPointCategories(
  caseData: Case
): { categories: TalkingPointCategory[]; classification: CaseClassification } {
  const classification = classifyCase(caseData);
  const categories: TalkingPointCategory[] = [];

  // Service-type base categories
  const builders = [
    buildDurationPersistence,
    buildFailedConservative,
    buildDiagnosticUncertainty,
    buildGuidelineSupport,
    buildRiskOfDelay,
    buildFunctionalLimitation,
    buildImagingFindings,
    buildImagingRationale,
    buildPriorMedFailures,
    buildContraindicationsToAlternatives,
    buildRiskIfUntreated,
    // Specialty overlays
    buildObgynOverlay,
    buildNeuroOverlay,
    buildMskOverlay,
    buildCardioOverlay,
  ];

  for (const builder of builders) {
    const category = builder(classification, caseData);
    if (category) {
      categories.push(category);
    }
  }

  // Enforce mandatory clinical reasoning order:
  //   1 = symptom persistence / functional limitation
  //   2 = failed first-line therapy
  //   3 = rule out serious pathology
  //   4 = imaging rationale / guideline match
  //   5 = risk of delay
  // Specialty overlays (2.5) slot between therapy and pathology.
  categories.sort((a, b) => a.order - b.order);

  // Deduplicate: if both imagingRationale and guidelineSupport exist,
  // keep imagingRationale for imaging cases, guidelineSupport otherwise
  const hasImagingRat = categories.some(c => c.id === "imagingRationale");
  const hasGuidelineSup = categories.some(c => c.id === "guidelineSupport");
  if (hasImagingRat && hasGuidelineSup) {
    const idx = categories.findIndex(c => c.id === "guidelineSupport");
    if (idx !== -1) categories.splice(idx, 1);
  }

  // Cap at 5 most relevant
  if (categories.length > 5) {
    return { categories: categories.slice(0, 5), classification };
  }

  return { categories, classification };
}

// ── Auto-selection logic ────────────────────────────────────

export function getAutoSelectedPills(
  categories: TalkingPointCategory[],
  caseData: Case
): Record<string, string[]> {
  const autoSelected: Record<string, string[]> = {};
  const tried = (caseData.tried_already || "").toLowerCase();
  const allText = [
    caseData.diagnoses,
    caseData.request_details,
    caseData.additional_context,
    caseData.clinical_history,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  for (const category of categories) {
    const selected: string[] = [];
    for (const pill of category.pills) {
      if (pill.isOther) continue;
      if (pill.autoSelectKeywords) {
        const text = category.id === "failedConservativeTx" ? tried : allText;
        if (pill.autoSelectKeywords.some((kw) => text.includes(kw))) {
          selected.push(pill.id);
        }
      }
    }
    if (selected.length > 0) {
      autoSelected[category.id] = selected;
    }
  }

  return autoSelected;
}

// ── Summary sentence builder ────────────────────────────────
// Generates 2–4 clean, spoken-aloud clinical bullets.
// Rules:
//   - One clinical idea per sentence
//   - Max ~14 words per bullet
//   - No prefix+label stitching (each pill → standalone sentence)
//   - Decisive verbs: Evaluate, Rule out, Exclude, Document, Requires
//   - No filler: "we need to", "consider", "evaluation for"
//   - No passive voice, no "we"

/** Sentence-framing strategies keyed by category ID pattern */
const CATEGORY_FRAMERS: Record<string, (label: string, extra?: string) => string> = {
  // Persistence categories: state duration/trajectory as fact
  durationPersistence: (label, extra) => {
    // If label already contains a duration ("Symptoms >6 weeks") and we have an extra,
    // use the extra as the actual duration instead
    if (extra && /\d+\s*week/i.test(label)) {
      return `Persistent symptoms for ${extra}.`;
    }
    if (extra) return `${capitalize(label)} for ${extra}.`;
    return `${capitalize(label)}.`;
  },
  functionalLimitation: (label) => `${capitalize(label)}.`,

  // Failed therapy: state what failed
  failedConservativeTx: (label, extra) => {
    // Labels that are already sentences (contain verbs) → use as-is
    if (/\b(failed|completed|tried|intolerant|contraindicated)\b/i.test(label)) {
      return `${capitalize(label)}.`;
    }
    // Treatment names like "Physical therapy", "NSAIDs" → frame as failure
    if (extra) return `${capitalize(label)} failed after ${extra}.`;
    return `${capitalize(label)} failed to provide adequate relief.`;
  },
  priorMedFailures: (label) => {
    // Labels like "Metformin — failed/intolerant" → clean up em dash
    if (label.includes("—")) {
      const parts = label.split("—").map(s => s.trim());
      return `${capitalize(parts[0])} ${parts[1]}.`;
    }
    return `${capitalize(label)}.`;
  },

  // Rule-out: most labels already start with "Rule out" / "Must exclude"
  ruleOutPathology: (label) => `${capitalize(label)}.`,
  diagnosticUncertainty: (label) => `${capitalize(label)}.`,

  // Imaging: labels like "Pelvic ultrasound is first-line imaging"
  imagingRationale: (label) => `${capitalize(label)}.`,
  imagingFindings: (label) => `${capitalize(label)}.`,

  // Guideline: labels like "ACOG recommends this as first-line"
  guidelineSupport: (label) => `${capitalize(label)}.`,

  // Risk: labels are now concrete harms ("Missed endometrial malignancy", "Acute hemorrhage")
  // Just capitalize and present as-is — they're already clean.
  riskOfDelay: (label) => `${capitalize(label)}.`,
  riskIfUntreated: (label) => `${capitalize(label)}.`,

  // Contraindications
  contraindications: (label) => `Alternatives precluded by ${label.toLowerCase()}.`,
};

function capitalize(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Clean filler and redundancy from a label before framing */
function cleanLabel(raw: string): string {
  let s = raw.trim();
  // Strip leading filler phrases
  s = s.replace(/^(we need to|we should|consider|should consider|evaluation for)\s+/i, "");
  // Collapse double spaces
  s = s.replace(/\s{2,}/g, " ");
  return s;
}

/** Build a single clinical sentence from a pill label + category context */
function framePill(
  categoryId: string,
  pillLabel: string,
  extraStr?: string,
): string {
  const label = cleanLabel(pillLabel);

  // Try category-specific framer
  const framer = CATEGORY_FRAMERS[categoryId];
  if (framer) {
    return framer(label, extraStr);
  }

  // Specialty overlay categories (obgynSpecific, neuroSpecific, etc.)
  // Labels are usually already standalone phrases
  return `${capitalize(label)}.`;
}

/** Score a pill for priority ranking (higher = more important) */
function pillPriority(categoryId: string, pillId: string): number {
  // Rule-out and risk categories are highest priority
  if (categoryId.includes("ruleOut") || categoryId.includes("diagnostic")) return 10;
  if (categoryId.includes("risk")) return 9;
  // Duration/persistence anchors the case
  if (categoryId === "durationPersistence") return 8;
  // Failed therapy is core to medical necessity
  if (categoryId.includes("failed") || categoryId.includes("priorMed")) return 7;
  // Imaging rationale
  if (categoryId.includes("imaging")) return 6;
  // Guideline support
  if (categoryId.includes("guideline")) return 5;
  // Everything else
  return 3;
}

export function buildSpeakableSummary(
  categories: TalkingPointCategory[],
  selectedPills: Record<string, string[]>,
  extraValues: Record<string, Record<string, string>>,
  _otherText: Record<string, string>
): string {
  // 1. Collect all selected pills as sentence candidates
  const candidates: Array<{
    sentence: string;
    priority: number;
    categoryId: string;
  }> = [];

  for (const config of categories) {
    const pills = selectedPills[config.id] || [];
    if (pills.length === 0) continue;

    // Build extras string for this category (duration, scores, etc.)
    const categoryExtras = extraValues[config.id] || {};
    let extraStr: string | undefined;
    for (const [key, value] of Object.entries(categoryExtras)) {
      if (!value) continue;
      const extraDef = config.extras?.find((e) => e.id === key);
      if (extraDef) {
        extraStr = `${value}${extraDef.suffix ? ` ${extraDef.suffix}` : ""}`;
        break; // Use first meaningful extra
      }
    }

    for (const pillId of pills) {
      const pill = config.pills.find((p) => p.id === pillId);
      if (!pill || pill.isOther) continue;

      const sentence = framePill(config.id, pill.label, extraStr);

      // Only use extraStr for the first pill in a category
      candidates.push({
        sentence,
        priority: pillPriority(config.id, pillId),
        categoryId: config.id,
      });

      // Clear extra after first use so it doesn't repeat
      extraStr = undefined;
    }
  }

  if (candidates.length === 0) return "";

  // 2. Sort by priority (descending), then deduplicate by category
  //    Take at most 2 pills per category, max 4 total
  candidates.sort((a, b) => b.priority - a.priority);

  const used: string[] = [];
  const catCount: Record<string, number> = {};
  const MAX_BULLETS = 4;
  const MAX_PER_CATEGORY = 2;

  for (const c of candidates) {
    if (used.length >= MAX_BULLETS) break;
    const count = catCount[c.categoryId] || 0;
    if (count >= MAX_PER_CATEGORY) continue;
    used.push(c.sentence);
    catCount[c.categoryId] = count + 1;
  }

  return used.join("\n");
}